import { useState, useEffect, useRef, useContext, useCallback } from "react"
import { X, Link2 } from "lucide-react"

// Firebase utilities — startConnectorTest lives in the europe-west2 connectors
// codebase, so it's reached through the regional callable handle.
import { db, funEU } from "../../../../utils/firebase"
import { AlertsContext } from "../../../../utils/providers/alerts"

import { Timeline } from "../../../timeline"
import { buildConnectorStages } from "./connector-timeline"

import "./connectors.scss"

const startConnectorTest = funEU.httpsCallable("startConnectorTest")

const isHttpUrl = (raw) => {
	try {
		const u = new URL(String(raw || "").trim())
		return u.protocol === "http:" || u.protocol === "https:"
	} catch {
		return false
	}
}

/**
 * Smart modal (new-quote chassis) for adding/retesting a connector. Paste a
 * tee-sheet URL, hit Connect, and watch the classify-and-validate pipeline run
 * live on the embedded timeline. The connector doc is pre-created client-side so
 * we can subscribe to its progress the instant the run starts; the cloud
 * function streams each step onto it.
 */
export default function AddConnectorModal({ entityKind, entityId, existingConnector, onClose }) {
	const isCourse = entityKind === "CMS_COURSE"
	const collectionPath = isCourse ? `CMS_courses/${entityId}` : `CMS_holidays/${entityId}`
	const { pushAlert } = useContext(AlertsContext)

	const [url, setUrl] = useState(existingConnector?.url || "")
	const [running, setRunning] = useState(false)
	const [connector, setConnector] = useState(existingConnector || null)
	const [summary, setSummary] = useState(null)
	const unsubRef = useRef(null)

	// Tear down any live subscription when the modal unmounts.
	useEffect(() => () => unsubRef.current && unsubRef.current(), [])

	const valid = isHttpUrl(url)

	const connect = useCallback(async () => {
		if (!valid || running) return
		setSummary(null)
		setRunning(true)

		const col = db.collection(`${collectionPath}/connectors`)
		// Pre-generate the doc id (or reuse the one being retested) so we can
		// subscribe to live progress before the callable returns.
		const id = existingConnector?.id || col.doc().id

		if (unsubRef.current) unsubRef.current()
		unsubRef.current = col.doc(id).onSnapshot((snap) => {
			if (snap.exists) setConnector({ id: snap.id, ...snap.data() })
		})

		try {
			const { data } = await startConnectorTest({
				entityKind,
				entityId,
				url: url.trim(),
				connectorId: id,
			})
			setSummary(data || { status: "failed" })
		} catch (error) {
			pushAlert({ type: "ERROR", title: "Connector test failed", body: error.message })
			setSummary({ status: "failed", reason: error.message })
		} finally {
			setRunning(false)
		}
	}, [valid, running, collectionPath, existingConnector, entityKind, entityId, url, pushAlert])

	const requestClose = () => {
		if (running) return
		onClose()
	}

	const stages = connector ? buildConnectorStages(connector) : []
	const phase = connector?.run?.phase
	const succeeded = summary?.status === "active" || connector?.status === "active"
	const failed = !running && (summary?.status === "failed" || phase === "failed")
	const errorText = connector?.run?.error || summary?.reason || "Couldn't establish a working connection."

	return (
		<div
			className="connector-modal-overlay"
			onMouseDown={(event) => event.target === event.currentTarget && requestClose()}>
			<div className="connector-modal">
				<div className="connector-modal-header">
					<div>
						<h3>{existingConnector ? "Retest connector" : "Add connector"}</h3>
						<p>Drop a live tee sheet in and the system will work out how to read it.</p>
					</div>
					<button
						type="button"
						className="connector-modal-close"
						onClick={requestClose}
						aria-label="Close">
						<X size={18} />
					</button>
				</div>

				<div className="connector-modal-body">
					{/* URL field + Connect — spinner sits on the far right of the field
					    while a run is in flight, mirroring the address-lookup input. */}
					<div className="connector-url-row">
						<div className={["connector-url-field", running && "is-busy"].filter(Boolean).join(" ")}>
							<span className="connector-url-icon">
								<Link2 size={15} />
							</span>
							<input
								className="connector-url-input"
								type="url"
								placeholder="https://…  paste a tee-sheet link"
								value={url}
								readOnly={running}
								onChange={(event) => setUrl(event.target.value)}
								onKeyDown={(event) => event.key === "Enter" && connect()}
							/>
							{running && <span className="connector-url-spinner" />}
						</div>
						<button
							type="button"
							className="connector-button connector-button--primary"
							onClick={connect}
							disabled={!valid || running}>
							{running ? "Connecting…" : failed ? "Retry" : "Connect"}
						</button>
					</div>

					{/* Live timeline */}
					{stages.length > 0 && (
						<div className="connector-timeline design-root">
							<Timeline stages={stages} />
						</div>
					)}

					{/* Result line */}
					{succeeded && (
						<div className="connector-result is-success">
							Connector validated and saved — it's now active on this {isCourse ? "course" : "holiday"}.
						</div>
					)}
					{failed && <div className="connector-result is-error">{errorText}</div>}
				</div>

				<div className="connector-modal-foot">
					{succeeded ? (
						<button
							type="button"
							className="connector-button connector-button--primary"
							onClick={onClose}>
							Done
						</button>
					) : (
						<button
							type="button"
							className="connector-button"
							onClick={requestClose}
							disabled={running}>
							Cancel
						</button>
					)}
				</div>
			</div>
		</div>
	)
}
